import { useCubeStore } from './cube'
import { FISCAL_YEAR_START_MONTH_DEFAULT_OPTION } from '~/constants/customization'
import { FETCH_CUSTOMIZATIONS_QUERY } from '~/gql/customizations/fetchCustomizations'
import { UPDATE_CUSTOMIZATIONS_MUTATION } from '~/gql/customizations/updateCustomizations'
import {
  CustomizationField,
  CustomizationFieldSetupOptions,
  type Customizations
} from '~/types/customization'
import { buildObjectFromEntries } from '~/types/utils'

interface State {
  customizations: Customizations | null
}

export const useCustomizationStore = defineStore('customization', {
  state: (): State => ({
    customizations: null
  }),
  getters: {
    getCustomizationByField(
      state: State
    ): (field: CustomizationField) => string {
      return field => state.customizations![field]
    },
    isRecruitmentDataAvailable: (state: State) => {
      return (
        state.customizations![
          CustomizationField.RECRUITMENT_ANALYTICS_FEATURE_TOGGLE
        ] === CustomizationFieldSetupOptions.TRUE
      )
    },
    isLegalReportsAvailable: (state: State) => {
      return (
        state.customizations![
          CustomizationField.LEGAL_REPORTS_FEATURE_TOGGLE
        ] === CustomizationFieldSetupOptions.TRUE
      )
    },
    isBenchmarkAvailable: (state: State) => {
      return (
        state.customizations![CustomizationField.BENCHMARK_FEATURE_TOGGLE] ===
        CustomizationFieldSetupOptions.TRUE
      )
    },
    isDashboardAvailable: (state: State) => {
      return (
        state.customizations![CustomizationField.DASHBOARD_FEATURE_TOGGLE] ===
        CustomizationFieldSetupOptions.TRUE
      )
    },
    fiscalYearStartMonth: (state: State) => {
      return Number(
        state.customizations![CustomizationField.FISCAL_YEAR_START_MONTH]
      )
    },
    isInFiscalMode: (state: State) => {
      return (
        state.customizations![CustomizationField.FISCAL_YEAR_START_MONTH] !==
        FISCAL_YEAR_START_MONTH_DEFAULT_OPTION
      )
    },
    isTrialPeriodDataAvailable: (state: State) => {
      return (
        state.customizations![CustomizationField.HAS_TRIAL_PERIOD_DATA] ===
        CustomizationFieldSetupOptions.TRUE
      )
    },
    isManagerDataAvailable: (state: State) => {
      return (
        state.customizations![CustomizationField.HAS_MANAGER_DATA] ===
        CustomizationFieldSetupOptions.TRUE
      )
    }
  },
  actions: {
    async loadCustomizations() {
      const app = useNuxtApp()

      const customizationObjects = (
        await app.$apiGqlClient.query({
          query: FETCH_CUSTOMIZATIONS_QUERY
        })
      ).data.getCustomizations as { field: CustomizationField; value: string }[]

      this.customizations = customizationObjects.reduce(
        (acc, { field, value }) => {
          acc[field] = value
          return acc
        },
        {} as Customizations
      )
    },
    async updateCustomizations(
      customizationsToUpdate: { field: CustomizationField; value: string }[]
    ) {
      const app = useNuxtApp()

      await app.$apiGqlClient.mutate({
        mutation: UPDATE_CUSTOMIZATIONS_MUTATION,
        variables: { customizations: customizationsToUpdate }
      })

      const newCustomizations = buildObjectFromEntries(
        customizationsToUpdate.map(customization => [
          customization.field,
          customization.value
        ])
      )

      this.customizations = {
        ...this.customizations,
        ...newCustomizations
      } as { [key in CustomizationField]: string }

      // Force meta reload because changes in customizations can impact analytics meta
      const { loadMeta } = useCubeStore()
      loadMeta(true)
    }
  }
})
